import type { LocalDate } from '@cadencia/contracts';

/**
 * Aritmetica sobre fechas del CALENDARIO LOCAL del usuario, en formato
 * `YYYY-MM-DD` (docs/DATA_MODEL.md §1.2).
 *
 * Vive en el dominio y opera en UTC POR DENTRO a proposito. Una fecha local es
 * una etiqueta de calendario, no un instante: «el dia 12» debe significar lo
 * mismo en Madrid en agosto que en enero. Construyendo la fecha con `Date.UTC`
 * el cambio de hora no existe, y sumar un dia siempre suma exactamente un dia.
 * Hacerlo con `new Date(y, m, d)` funciona casi siempre y falla la madrugada
 * del cambio de hora, que es la peor forma de fallar: rara y silenciosa.
 *
 * Estas funciones NO leen el reloj (ADR-007). Quien necesite «hoy» lo pasa.
 */

const PATRON = /^(\d{4})-(\d{2})-(\d{2})$/;

function partes(value: LocalDate): [number, number, number] {
  const match = PATRON.exec(value);
  if (!match) throw new Error(`Fecha local no valida: ${value}`);
  const [, year, month, day] = match;
  return [Number(year), Number(month), Number(day)];
}

function aMillis(value: LocalDate): number {
  const [year, month, day] = partes(value);
  return Date.UTC(year, month - 1, day);
}

function aTexto(millis: number): LocalDate {
  return new Date(millis).toISOString().slice(0, 10);
}

/** `true` si la cadena tiene forma de fecha local y existe en el calendario. */
export function isLocalDate(value: string): boolean {
  const match = PATRON.exec(value);
  if (!match) return false;
  // Comprobar que no se ha desbordado: 2026-02-30 encaja con el patron pero no
  // existe, y `Date.UTC` lo convertiria en marzo sin protestar.
  return aTexto(aMillis(value)) === value;
}

export function addLocalDays(value: LocalDate, days: number): LocalDate {
  return aTexto(aMillis(value) + Math.trunc(days) * 86_400_000);
}

/** Dias de `from` a `to`. Negativo si `to` es anterior. */
export function daysBetweenLocal(from: LocalDate, to: LocalDate): number {
  return Math.round((aMillis(to) - aMillis(from)) / 86_400_000);
}

export function compareLocalDates(a: LocalDate, b: LocalDate): number {
  // Las fechas en `YYYY-MM-DD` ordenan bien como texto, pero se valida el
  // formato para que una cadena basura falle aqui y no mas adelante.
  aMillis(a);
  aMillis(b);
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Dia de la semana con 0 = lunes, para que «fin de semana» sea 5 y 6. */
export function weekdayIndex(value: LocalDate): number {
  return (new Date(aMillis(value)).getUTCDay() + 6) % 7;
}

/** Lunes de la semana a la que pertenece la fecha. */
export function startOfLocalWeek(value: LocalDate): LocalDate {
  return addLocalDays(value, -weekdayIndex(value));
}

/** Los siete dias de la semana que empieza en `weekStart`. */
export function daysOfWeek(weekStart: LocalDate): LocalDate[] {
  return Array.from({ length: 7 }, (_, index) => addLocalDays(weekStart, index));
}
